import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import applyAppTokenRefreshInterceptor from "./axios.refresh_token";
import apiConfig from "@callveriq/config/api.config";

export const apiConfigs = {
    HTTPS: "https://",
    APPURL: "https://app",
    BASEURL: "api.callveriq.com",
} as const;

interface DomainMappingConfig {
    [key: string]: string;
}

const domainMappingConfig: DomainMappingConfig = {
    voiceanalysis1: "mahindrafirstchoice",
};

const getDomain = (): string => {
    const { hostname } = new URL(window.location.href);
    return hostname.split(".")?.[0] ?? "";
};

export const getDomainMappingName = (name: string): string => {
    return domainMappingConfig?.[name?.toLowerCase()] || name;
};

const protocol: string =
    import.meta.env.VITE_APP_API_PROTOCOL ?? apiConfigs.HTTPS;
const apiBaseurl: string =
    import.meta.env.VITE_APP_BACKEND_BASE_URL ?? apiConfigs.BASEURL;

const baseUrl: string = import.meta.env.VITE_APP_BACKEND_DOMAIN
    ? `${protocol}${import.meta.env.VITE_APP_BACKEND_DOMAIN}`
    : `${protocol}${getDomainMappingName(getDomain())}.${apiBaseurl}`;

const axiosInstance = axios.create({
    baseURL: import.meta.env.VITEST ? apiConfig.TESTURL : baseUrl,
});

interface AuthTokens {
    access: string;
    refresh: string;
}

const addHeader = async (
    config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
    const authTokensString = localStorage.getItem("authTokens");
    const authTokens: AuthTokens | null = authTokensString
        ? (JSON.parse(authTokensString) as AuthTokens)
        : null;

    let authHeader: Record<string, string> = {};
    if (authTokens?.access) {
        authHeader = { Authorization: `Bearer ${authTokens.access}` };
    }

    config.headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...config.headers,
        ...authHeader,
    } as InternalAxiosRequestConfig["headers"];

    return config;
};

axiosInstance.interceptors.request.use(addHeader, (error) => {
    return Promise.reject(error);
});

applyAppTokenRefreshInterceptor(axiosInstance);

interface BaseQueryArgs {
    method: string;
    url: string;
    body?: unknown;
    data?: unknown;
    [key: string]: unknown;
}

interface ApiOptions {
    [key: string]: unknown;
}

interface ExtraOptions {
    [key: string]: unknown;
}

const baseQuery = async (
    args: BaseQueryArgs,
    _apiOptions: ApiOptions,
    _extraOptions: ExtraOptions
): Promise<Response> => {
    if (
        args.method === "POST" ||
        args.method === "PUT" ||
        args.method === "PATCH"
    ) {
        args.data = JSON.stringify(args.body);
    }
    const axiosResponse: AxiosResponse = await axiosInstance.request(args);

    const { headers, status, statusText } = axiosResponse;
    const data = axiosResponse.data;

    const response = new Response(JSON.stringify(data), {
        headers: headers as HeadersInit,
        status,
        statusText,
    });

    return response;
};

export { axiosInstance, baseQuery, baseUrl };
